/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-25
 */

import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

// Add local dependencies.
import {
  PaymentProvider,
  StripeConfig,
  PaymentConfiguration,
  StripeError,
} from '../../../types/payment';

// Stripe Configuration
const stripeConfig: StripeConfig = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  options: {
    locale: 'en' as const,
  },
};

// Payment Provider Context
const PaymentProviderContext = createContext<PaymentProvider | null>(null);

// Custom hook to use payment provider
export const usePaymentProvider = (): PaymentProvider => {
  const context = useContext(PaymentProviderContext);
  if (!context) {
    throw new Error(
      'usePaymentProvider must be used within a SecurePaymentProvider'
    );
  }
  return context;
};

// Stripe instance (singleton)
let stripePromise: Promise<Stripe | null> | null = null;

const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    if (!stripeConfig.publishableKey) {
      console.error(
        'Stripe publishable key is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable.'
      );
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(
      stripeConfig.publishableKey,
      stripeConfig.options
    );
  }
  return stripePromise;
};

// Component Props
interface SecurePaymentProviderProps {
  children: React.ReactNode;
  configuration?: PaymentConfiguration;
  onError?: (error: StripeError) => void;
  fallback?: React.ReactNode;
}

// Error Boundary Component
interface PaymentErrorBoundaryState {
  hasError: boolean;
}

interface PaymentErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: StripeError) => void;
}

class PaymentErrorBoundary extends React.Component<
  PaymentErrorBoundaryProps,
  PaymentErrorBoundaryState
> {
  constructor(props: PaymentErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): PaymentErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Payment Error Boundary caught an error:', error, errorInfo);

    const { onError } = this.props;
    if (onError) {
      onError({
        type: 'api_error',
        message: error.message || 'An unexpected payment error occurred',
        code: 'payment_provider_error',
      });
    }
  }

  render(): React.ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="200px"
          p={3}
        >
          <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Payment System Unavailable
            </Typography>
            <Typography variant="body2">
              We&apos;re experiencing technical difficulties with our payment
              system. Please try again later or contact support if the problem
              persists.
            </Typography>
          </Alert>
        </Box>
      );
    }

    return children;
  }
}

// Loading Component
const PaymentProviderLoading: React.FC<{ message?: string }> = ({
  message = 'Initializing secure payment system...',
}) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="200px"
    p={3}
  >
    <CircularProgress size={40} sx={{ mb: 2 }} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

// Main Provider Component
export const SecurePaymentProvider = ({
  children,
  configuration,
  onError,
  fallback,
}: SecurePaymentProviderProps): React.JSX.Element => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        const stripeInstance = await getStripe();

        if (!stripeInstance) {
          throw new Error(
            'Failed to initialize Stripe. Please check your configuration.'
          );
        }

        setStripe(stripeInstance);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);

        if (onError) {
          onError({
            type: 'api_error',
            message: errorMessage,
            code: 'stripe_initialization_error',
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeStripe();
  }, [onError]);

  // Stripe Elements options
  const elementsOptions: StripeElementsOptions = useMemo(
    () => ({
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#1976d2', // Material-UI primary color
          colorBackground: '#ffffff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          fontFamily: 'Arial, sans-serif',
          spacingUnit: '4px',
          borderRadius: '4px',
          ...configuration?.appearance?.variables,
        },
        rules: {
          '.Input': {
            border: '1px solid #e6ebf1',
            borderRadius: '4px',
            padding: '12px',
            fontSize: '16px',
          },
          '.Input:focus': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)',
          },
          '.Input--invalid': {
            borderColor: '#df1b41',
          },
          '.Label': {
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '8px',
            color: '#30313d',
          },
          ...configuration?.appearance?.rules,
        },
      },
      loader: configuration?.loader || 'auto',
      clientSecret: configuration?.clientSecret,
    }),
    [configuration]
  );

  // Provider value
  const providerValue: PaymentProvider = useMemo(
    () => ({
      stripe,
      elements: null, // Will be provided by Elements component
      isLoading,
      error,
    }),
    [stripe, isLoading, error]
  );

  // Handle loading state
  if (isLoading) {
    return (fallback as React.JSX.Element) || <PaymentProviderLoading />;
  }

  // Handle error state
  if (error || !stripe) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        p={3}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Payment System Error
          </Typography>
          <Typography variant="body2">
            {error ||
              'Unable to initialize payment system. Please refresh the page and try again.'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <PaymentErrorBoundary onError={onError}>
      <PaymentProviderContext.Provider value={providerValue}>
        <Elements stripe={stripe} options={elementsOptions}>
          {children}
        </Elements>
      </PaymentProviderContext.Provider>
    </PaymentErrorBoundary>
  );
};

// Connection Status Hook
export const useStripeConnectionStatus = () => {
  const { stripe, isLoading, error } = usePaymentProvider();

  return {
    isConnected: !!stripe && !isLoading && !error,
    isLoading,
    error,
    stripe,
  };
};

// Environment Check Hook
export const usePaymentEnvironment = () => {
  const isTestMode = stripeConfig.publishableKey.includes('pk_test_');
  const isLiveMode = stripeConfig.publishableKey.includes('pk_live_');
  const isConfigured = !!stripeConfig.publishableKey;

  let environment: string;
  if (isLiveMode) {
    environment = 'live';
  } else if (isTestMode) {
    environment = 'test';
  } else {
    environment = 'unknown';
  }

  return {
    isTestMode,
    isLiveMode,
    isConfigured,
    environment,
  };
};

export default SecurePaymentProvider;
